import React, { useState, useRef, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useAdmin } from '../context/AdminContext';
import { User, Shield, Building, LogOut, ChevronDown, Mail, UserCheck } from 'lucide-react';

const UserProfileDropdown = ({ className = "" }) => {
  const { keycloak } = useKeycloak();
  const { tenantName, domain, isAdmin, adminInfo } = useAdmin();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const userEmail = keycloak.tokenParsed?.email;
  const userName = keycloak.tokenParsed?.name || userEmail?.split('@')[0];

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleLogout = () => {
    keycloak.logout({
      redirectUri: window.location.origin
    });
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
          <User size={16} className="text-white" />
        </div>
        <div className="hidden md:block text-left">
          <div className="text-sm font-medium text-gray-800">{userName}</div>
          <div className="text-xs text-gray-500 flex items-center space-x-1">
            {tenantName && (
              <>
                <Building size={10} />
                <span>{tenantName}</span>
                {isAdmin && <Shield size={10} className="text-blue-600" />}
              </>
            )}
          </div>
        </div>
        <ChevronDown size={14} className="text-gray-400" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50">
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <User size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-800 truncate">{userName}</div>
                <div className="text-sm text-gray-500 truncate" title={userEmail}>{userEmail}</div>
              </div>
            </div>
          </div>

          {/* Tenant Info Section */}
          {tenantName && (
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="text-xs text-gray-500 mb-2">WORKSPACE</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Building size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-700">{tenantName}</span>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center space-x-1 px-2 py-1 bg-blue-100 rounded-full">
                      <Shield size={10} className="text-blue-600" />
                      <span className="text-xs text-blue-600 font-medium">Admin</span>
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-500">{domain}</div>
              </div>
            </div>
          )}

          {/* Admin Contact Info for Regular Users */}
          {!isAdmin && adminInfo && (
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="text-xs text-gray-500 mb-2">YOUR ADMIN</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <UserCheck size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-700">{adminInfo.name || adminInfo.email?.split('@')[0]}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2 min-w-0">
                  <Mail size={12} className="text-gray-400 flex-shrink-0" />
                  <a 
                    href={`mailto:${adminInfo.email}?subject=Support Request&body=Hi, I need assistance with my account.`}
                    className="text-xs text-blue-600 hover:text-blue-800 underline truncate"
                    title={adminInfo.email}
                    onClick={() => setIsOpen(false)}
                  >
                    {adminInfo.email}
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <LogOut size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;